import { useGetTeam } from "@/hooks/queryHooks/teamHooks";
import { useUserList } from "@/hooks/queryHooks/userHooks";
import { useTenantStore } from "@/stores/tenantStore";
import type { Presenter, UserWRole } from "@repo/types";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInputOptional } from "@repo/ui/components/Base/SelectInputOptional";
import { useMemo } from "react";

export interface SelectPresenterProps {
  value: Presenter | string | undefined;
  onChange?: (value: Presenter) => void;
  team: string | undefined;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  className?: string;
}

export const SelectPresenter = ({
  value,
  onChange,
  team,
  label,
  disabled,
  error,
  errorMessage,
  className,
}: SelectPresenterProps) => {
  const tenantId = useTenantStore((s) => s.id);
  const { data: teamData } = useGetTeam(tenantId, team ?? "");
  const { data: userData } = useUserList(tenantId);

  const userMap = useMemo(
    () =>
      userData?.reduce(
        (obj, user) => ((obj[user.id] = user), obj),
        Object.create(null) as Record<string, UserWRole>
      ),
    [userData]
  );

  return (
    <SelectInputOptional
      disabled={disabled}
      containerStyle={className}
      label={label ?? "Presenter"}
      otherValue="null"
      manualPlaceholder="Presenter's name"
      selectValue={
        typeof value === "string" // upgrade old version
          ? value || "none"
          : value && value?.userId == null
            ? "null"
            : value?.userId || "none" || ""
      }
      manualValue={(value as Presenter)?.manualEntry ?? ""}
      onChange={(userId, manualEntry) => {
        const value: Presenter = {};

        if (userId === "null") {
          value.manualEntry = manualEntry;
        } else {
          value.userId = userId;
        }

        onChange?.(value);
      }}
      options={[
        <SelectItem key="none" value="none">
          Not Assigned
        </SelectItem>,
        ...(teamData?.teamMembers.map((member) => {
          const user = userMap?.[member.userId];

          return (
            <SelectItem key={member.userId} value={member.userId}>
              {user?.firstName} {user?.lastName} ({user?.email})
            </SelectItem>
          );
        }) ?? []),
      ]}
      error={error}
      errorMessage={errorMessage}
    />
  );
};
